import { FC } from "react"
import { useFormik } from "formik"
import Modal from "@/components/Modal"
import TextField from "@/components/TextField"
import Button from "@/components/Button"
import OutlineButton from "@/components/OutlineButton"
import validationSchema from "../utils/validation-schema"
import Select from "@/components/Select"
import Switch from "@/components/Switch"
import { useAdminUsersInfiniteScroll } from "@/pages/Organizations/hooks/useAdminUsersInfiniteScroll"
import { useAdminEditOrganization } from "@/pages/Organizations/hooks/useAdminEditOrganization"
import type { AdminOrganization } from "@/types/entities/organization"

// Define available pages for permissions - updated with actual app pages
const AVAILABLE_PAGES = [
  { label: "Dashboard", value: "/dashboard" },
  { label: "Prospects", value: "/prospects" },
  { label: "Campaigns", value: "/campaigns" },
  { label: "Sequences", value: "/sequences" },
  { label: "Search Criteria", value: "/search-criteria" },
  { label: "Audiences", value: "/master-lists" },
  { label: "Senders", value: "/senders" },
  { label: "Email Addresses", value: "/emails" },
  { label: "Domains", value: "/domains" },
  { label: "Domains / Inboxes", value: "/inboxing-domains" },
  { label: "DNC List", value: "/dnc" },
]

type Props = {
  open: boolean
  data?: AdminOrganization
  onClose: () => void
  onEditSuccess?: (response: AdminOrganization) => void
}

const AdminEditOrganizationModal: FC<Props> = ({
  open,
  onClose,
  data,
  onEditSuccess,
}: Props) => {
  const { editOrganization, response } = useAdminEditOrganization()
  const {
    isLoading: isUsersLoading,
    onUsersScroll,
    paginatedOwners,
  } = useAdminUsersInfiniteScroll()

  // Remove the additional query that was causing issues
  // Just use the data passed from the parent component
  const orgData = data
  const hasOrgData = Boolean(orgData?._id)

  // Ensure permission fields exist with empty arrays as fallbacks
  const safeOrgData = {
    ...(orgData || ({} as AdminOrganization)),
    _id: orgData?._id ?? "",
    name: orgData?.name ?? "",
    url: orgData?.url ?? "",
    catchAll: orgData?.catchAll ?? "",
    fullAccessUsers: Array.isArray(orgData?.fullAccessUsers)
      ? orgData.fullAccessUsers
      : [],
    editAccessUsers: Array.isArray(orgData?.editAccessUsers)
      ? orgData.editAccessUsers
      : [],
    editAccessPages: Array.isArray(orgData?.editAccessPages)
      ? orgData.editAccessPages
      : [],
    readAccessUsers: Array.isArray(orgData?.readAccessUsers)
      ? orgData.readAccessUsers
      : [],
    readAccessPages: Array.isArray(orgData?.readAccessPages)
      ? orgData.readAccessPages
      : [],
    specialPermissions: orgData?.specialPermissions || {},
  }

  // Helper to extract DNC List edit permission from various possible structures
  const extractDncListEditPermission = (permissions: any): boolean => {
    if (!permissions) return false

    // Handle the backend structure
    if (permissions.editDncList && permissions.editDncList.allowEdit) {
      return true
    }

    // Handle the frontend structure
    if (permissions.dncListEdit === true) {
      return true
    }

    return false
  }

  const {
    handleSubmit,
    getFieldProps,
    isValid,
    dirty,
    resetForm,
    values,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: safeOrgData.name ?? "",
      owner: safeOrgData.owner?._id ?? "",
      url: safeOrgData.url ?? "",
      catchAll: safeOrgData.catchAll ?? "",
      fullAccessUsers: safeOrgData.fullAccessUsers,
      editAccessUsers: safeOrgData.editAccessUsers,
      editAccessPages: safeOrgData.editAccessPages,
      readAccessUsers: safeOrgData.readAccessUsers,
      readAccessPages: safeOrgData.readAccessPages,
      specialPermissions: {
        dncListEdit: extractDncListEditPermission(
          safeOrgData.specialPermissions,
        ),
      },
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async ({
      name,
      owner,
      url,
      catchAll,
      fullAccessUsers,
      editAccessUsers,
      editAccessPages,
      readAccessUsers,
      readAccessPages,
      specialPermissions,
    }) => {
      try {
        if (!safeOrgData._id) {
          throw new Error("Organization ID is required")
        }

        // Convert frontend specialPermissions structure to backend structure
        const backendSpecialPermissions = {
          editDncList: {
            allowRead: true,
            allowEdit: specialPermissions?.dncListEdit || false,
          },
        }

        const response = await editOrganization({
          id: safeOrgData._id,
          name,
          owner,
          url,
          catchAll,
          fullAccessUsers,
          editAccessUsers,
          editAccessPages,
          readAccessUsers,
          readAccessPages,
          specialPermissions: backendSpecialPermissions as any,
        })

        if (response?.data) {
          // Create a safe copy of the response data with all expected fields
          const updatedData = {
            ...response.data,
            fullAccessUsers: Array.isArray(response.data.fullAccessUsers)
              ? response.data.fullAccessUsers
              : [],
            editAccessUsers: Array.isArray(response.data.editAccessUsers)
              ? response.data.editAccessUsers
              : [],
            editAccessPages: Array.isArray(response.data.editAccessPages)
              ? response.data.editAccessPages
              : [],
            readAccessUsers: Array.isArray(response.data.readAccessUsers)
              ? response.data.readAccessUsers
              : [],
            readAccessPages: Array.isArray(response.data.readAccessPages)
              ? response.data.readAccessPages
              : [],
            specialPermissions: response.data.specialPermissions || {},
          }

          onEditSuccess && onEditSuccess(updatedData)
        } else {
          // If we don't get response data, at least pass back what we sent
          const fallbackData = {
            ...safeOrgData,
            name,
            url,
            catchAll,
            fullAccessUsers,
            editAccessUsers,
            editAccessPages,
            readAccessUsers,
            readAccessPages,
            specialPermissions: {
              dncListEdit: specialPermissions?.dncListEdit || false,
            },
          }
          onEditSuccess && onEditSuccess(fallbackData as any)
        }

        handleClose()
      } catch {
        // Mutation errors are reflected in the hook response state.
      }
    },
  })

  if (!hasOrgData) {
    return null
  }

  const handleClose = () => {
    resetForm()
    onClose && onClose()
  }

  // Convert user IDs to options for select components
  const userOptions = paginatedOwners.map(({ _id, firstName, lastName }) => ({
    label: `${firstName} ${lastName}`,
    value: _id,
  }))

  // CSS fix to prevent label clicks from opening the dropdown
  const labelStyle = {
    pointerEvents: "none" as const,
    marginBottom: "4px",
    display: "block",
    fontWeight: 500,
    fontSize: "14px", // Make font size smaller to match other field labels
  }

  const selectWrapperStyle = {
    position: "relative" as const,
  }

  return (
    <Modal
      open={open}
      title="Edit Organization"
      onClose={handleClose}
      actions={
        <div className="flex gap-4">
          <OutlineButton
            onClick={handleClose}
            className="text-black btn-sm !w-[86px]"
            type="button"
            disabled={response.isLoading}
          >
            Cancel
          </OutlineButton>
          <Button
            disabled={!(isValid && dirty) || response.isLoading}
            className="btn-sm !w-[86px]"
            type="submit"
          >
            {response.isLoading ? "Loading..." : "Save"}
          </Button>
        </div>
      }
      onSubmit={handleSubmit}
    >
      <div className="space-y-5">
        <TextField
          type="text"
          topLeftLabel="Name"
          placeholder="Enter organization name"
          rootClassName="[&_label.label]:pt-0"
          className="input-sm"
          autoComplete="off"
          {...getFieldProps("name")}
        />
        <TextField
          type="url"
          topLeftLabel="Website"
          placeholder="Enter organization URL"
          className="input-sm"
          {...getFieldProps("url")}
        />
        <div style={selectWrapperStyle}>
          <label style={labelStyle}>Owner</label>
          <Select
            className="bg-input h-10 rounded-md"
            bordered={false}
            placeholder="Select owner"
            loading={isUsersLoading}
            options={userOptions}
            getPopupContainer={(node) => node.parentNode}
            value={values.owner || null}
            onChange={(v) => setFieldValue("owner", v)}
            onPopupScroll={onUsersScroll}
          />
        </div>
        <TextField
          type="email"
          autoComplete="new-password"
          topLeftLabel="Catch-All Email"
          placeholder="Enter catch-all email"
          className="input-sm"
          {...getFieldProps("catchAll")}
        />

        {/* Permission Section */}
        <div className="border-t pt-4 mt-4">
          <h3 className="font-medium text-lg mb-4">User Permissions</h3>
          <p className="text-sm text-gray-600 mb-4">
            Configure which users have access to this organization and what
            level of access they have. Users should only be added to one
            permission group (the highest level they need).
          </p>

          {/* Full Access Users */}
          <div className="mb-4" style={selectWrapperStyle}>
            <label style={labelStyle}>Full Access Users</label>
            <Select
              className="bg-input rounded-md"
              bordered={false}
              placeholder="Select users with full access"
              loading={isUsersLoading}
              mode="multiple"
              options={userOptions}
              getPopupContainer={(node) => node.parentNode}
              value={values.fullAccessUsers || []}
              onChange={(v) => setFieldValue("fullAccessUsers", v)}
              onPopupScroll={onUsersScroll}
            />
            <p className="text-xs text-gray-500 mt-1">
              Full access users can view and edit all pages and features.
            </p>
          </div>

          {/* Edit Access Users */}
          <div className="mb-4" style={selectWrapperStyle}>
            <label style={labelStyle}>Edit Access Users</label>
            <Select
              className="bg-input rounded-md"
              bordered={false}
              placeholder="Select users with edit access"
              loading={isUsersLoading}
              mode="multiple"
              options={userOptions}
              getPopupContainer={(node) => node.parentNode}
              value={values.editAccessUsers || []}
              onChange={(v) => setFieldValue("editAccessUsers", v)}
              onPopupScroll={onUsersScroll}
            />
            <p className="text-xs text-gray-500 mt-1">
              Edit access users can view all accessible pages and edit specific
              pages.
            </p>
          </div>

          {/* Edit Access Pages */}
          <div className="mb-4" style={selectWrapperStyle}>
            <label style={labelStyle}>Edit Access Pages</label>
            <Select
              className="bg-input rounded-md"
              bordered={false}
              placeholder="Select pages that edit users can modify"
              mode="multiple"
              options={AVAILABLE_PAGES}
              getPopupContainer={(node) => node.parentNode}
              value={values.editAccessPages || []}
              onChange={(v) => setFieldValue("editAccessPages", v)}
            />
            <p className="text-xs text-gray-500 mt-1">
              These are the pages that users with edit access can modify.
            </p>
          </div>

          {/* Read Access Users */}
          <div className="mb-4" style={selectWrapperStyle}>
            <label style={labelStyle}>Read-Only Access Users</label>
            <Select
              className="bg-input rounded-md"
              bordered={false}
              placeholder="Select users with read-only access"
              loading={isUsersLoading}
              mode="multiple"
              options={userOptions}
              getPopupContainer={(node) => node.parentNode}
              value={values.readAccessUsers || []}
              onChange={(v) => setFieldValue("readAccessUsers", v)}
              onPopupScroll={onUsersScroll}
            />
            <p className="text-xs text-gray-500 mt-1">
              Read-only users can only view specific pages without editing
              capabilities.
            </p>
          </div>

          {/* Read Access Pages */}
          <div className="mb-4" style={selectWrapperStyle}>
            <label style={labelStyle}>Read-Only Access Pages</label>
            <Select
              className="bg-input rounded-md"
              bordered={false}
              placeholder="Select pages that read-only users can view"
              mode="multiple"
              options={AVAILABLE_PAGES}
              getPopupContainer={(node) => node.parentNode}
              value={values.readAccessPages || []}
              onChange={(v) => setFieldValue("readAccessPages", v)}
            />
            <p className="text-xs text-gray-500 mt-1">
              These are the pages that both read-only and edit access users can
              view. Edit access users can additionally edit pages selected in
              "Edit Access Pages".
            </p>
          </div>

          {/* Special Permissions */}
          <div className="mb-4">
            <h4 className="font-medium mb-2">Special Permissions</h4>
            <div className="flex items-center">
              <Switch
                checked={values.specialPermissions?.dncListEdit}
                onChange={(checked) =>
                  setFieldValue("specialPermissions.dncListEdit", checked)
                }
              />
              <span className="ml-2">
                Allow read-only users to edit DNC list
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This gives read-only users the ability to edit the DNC list
              specifically, while maintaining read-only access to other pages.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AdminEditOrganizationModal
